// src/ForgotPasswordPage.js
// MyTool NOW — Forgot Password Page
// Submits email to /api/auth/forgot-password
// Always shows success message (backend never reveals if email exists)

import { useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5002';

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Segoe UI', sans-serif",
    padding: '20px',
  },
  card: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '12px',
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
  },
  logo: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logoText: {
    fontSize: '24px',
    fontWeight: '800',
    color: '#FFD700',
    letterSpacing: '1px',
  },
  logoSub: {
    fontSize: '12px',
    color: '#2da8ff',
    marginTop: '4px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  title: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#2da8ff',
    marginBottom: '10px',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '14px',
    color: '#888',
    marginBottom: '24px',
    textAlign: 'center',
    lineHeight: '1.5',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '600',
    color: '#aaa',
    marginBottom: '6px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: '#111',
    border: '1px solid #444',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '15px',
    marginBottom: '16px',
    boxSizing: 'border-box',
    outline: 'none',
  },
  button: {
    width: '100%',
    padding: '14px',
    backgroundColor: 'black',
    color: '#FFA500',
    border: '4px solid #E8761D',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    letterSpacing: '1px',
    marginTop: '8px',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  error: {
    backgroundColor: '#3a0000',
    border: '1px solid #ff4444',
    borderRadius: '8px',
    padding: '12px 14px',
    color: '#ff6666',
    fontSize: '14px',
    marginBottom: '16px',
    textAlign: 'center',
  },
  success: {
    backgroundColor: '#003a00',
    border: '1px solid #44ff44',
    borderRadius: '8px',
    padding: '16px',
    color: '#66ff66',
    fontSize: '14px',
    marginBottom: '16px',
    textAlign: 'center',
    lineHeight: '1.6',
  },
  backLink: {
    display: 'block',
    textAlign: 'center',
    marginTop: '20px',
    color: '#2da8ff',
    fontSize: '13px',
    cursor: 'pointer',
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    letterSpacing: '0.5px',
  },
};

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    setError('');
    const trimEmail = email.trim().toLowerCase();

    if (!trimEmail) {
      setError('Please enter your email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimEmail }),
      });

      // Always show success (backend never reveals if email exists)
      setSubmitted(true);
    } catch (e) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <div style={styles.page}>
      <style>{`
        .jfn-forgot-btn:hover { background-color: #FFC107 !important; color: #000 !important; }
      `}</style>
      <div style={styles.card}>
        <div style={styles.logo}>
          <img
            src="/mtnlogo.png"
            alt="MyTool NOW"
            style={{ width: '220px', height: 'auto', display: 'block', margin: '0 auto', filter: 'drop-shadow(0 0 14px rgba(232,118,29,0.6))' }}
          />
          <div style={styles.logoSub}>SERVICE BUSINESS MANAGEMENT SOFTWARE</div>
        </div>

        <div style={styles.title}>Reset Password</div>
        <div style={styles.subtitle}>
          Enter your email and we'll send you a link to reset your password.
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {submitted ? (
          <div style={styles.success}>
            ✅ If that email is registered, a reset link has been sent.<br /><br />
            Check your inbox and follow the link within {60} minutes.
          </div>
        ) : (
          <>
            <label style={styles.label}>Email</label>
            <input
              style={styles.input}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={loading}
            />

            <button
              className="jfn-forgot-btn"
              style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? '⏳ SENDING...' : 'SEND RESET LINK'}
            </button>
          </>
        )}

        <button
          style={styles.backLink}
          onClick={() => window.location.href = '/login'}
        >
          ← Back to Sign In
        </button>
      </div>
    </div>
  );
}